/**
 * @file aop.ts
 * @remarks AOP 模块，提供数据获取后的处理功能，包括重置值、执行回调、显示通知等
 * @author ickeep <i@ickeep.com>
 * @module @yimoka/store
 */

import { FieldFeedbackTypes } from '@formily/core';
import { IHTTPResponse, isSuccess, isVacuous, mergeWithArrayOverride } from '@yimoka/shared';

import { cloneDeep } from 'lodash-es';

import { BaseStore } from './base';

/**
 * 处理数据获取后的操作
 * @param res - HTTP 响应数据
 * @param store - 存储实例
 * @remarks 按顺序执行重置值、执行回调、显示通知、表单错误同步等操作
 * @example
 * ```ts
 * const store = new BaseStore({
 *   afterAtFetch: {
 *     resetValues: 'success',
 *     notify: true,
 *     successRun: (res) => {
 *       console.log('数据获取成功:', res);
 *     },
 *     formErrorSync: {
 *       disabled: false,
 *       statusCode: 400,
 *       dataField: 'metadata'
 *     }
 *   }
 * });
 * ```
 */
export const handleAfterAtFetch = (res: Partial<IHTTPResponse>, store: BaseStore) => {
  handleResetValues(res, store);
  handleAfterAtFetchRun(res, store);
  handleAfterAtFetchNotify(res, store);
  handleFormErrorSync(res, store);
};

/**
 * 处理重置值的操作
 * @param res - HTTP 响应数据
 * @param store - 存储实例
 * @remarks 根据配置和响应状态决定是否重置表单值
 */
const handleResetValues = (res: Partial<IHTTPResponse>, store: BaseStore) => {
  const { resetValues } = store.afterAtFetch;
  const reset = () => {
    store.resetValues?.();
    store.form?.clearErrors();
  };

  if (resetValues === true) {
    reset();
    return;
  }

  if (isSuccess(res)) {
    if (resetValues === 'success') {
      reset();
    }
  } else if (resetValues === 'fail') {
    reset();
  }
};

/**
 * 处理数据获取后的回调函数
 * @param res - HTTP 响应数据
 * @param store - 存储实例
 * @remarks 根据响应状态执行相应的回调函数
 */
const handleAfterAtFetchRun = (res: Partial<IHTTPResponse>, store: BaseStore) => {
  const { run, failRun, successRun } = store.afterAtFetch;

  if (typeof run === 'function') {
    run(res, store);
  }

  if (isSuccess(res)) {
    if (typeof successRun === 'function') {
      successRun(res, store);
    }
  } else if (typeof failRun === 'function') {
    failRun(res, store);
  }
};

/**
 * 处理数据获取后的通知
 * @param res - HTTP 响应数据
 * @param store - 存储实例
 * @remarks 根据响应状态显示相应的通知消息
 */
const handleAfterAtFetchNotify = (res: Partial<IHTTPResponse>, store: BaseStore) => {
  const { notify, failNotify: notifyOnFail = notify, successNotify: notifyOnSuccess = notify } = store.afterAtFetch;

  const getMsg = (notify: boolean | string | undefined, df: string): string => {
    if (!notify) return '';
    if (notify === true) return res.msg || df;
    return notify;
  };

  const success = res?.success ?? isSuccess(res);
  if (success) {
    const msg = getMsg(notifyOnSuccess, '成功了');
    if (msg && store.notifier) {
      store.notifier('success', msg);
    }
  } else {
    const msg = getMsg(notifyOnFail, '出错了');
    if (msg && store.notifier) {
      store.notifier('error', msg);
    }
  }
};

/**
 * 处理表单错误同步
 * @param res - HTTP 响应数据
 * @param store - 存储实例
 * @remarks 当后端返回参数校验错误时，将错误信息同步到表单状态
 */

const handleFormErrorSync = (res: Partial<IHTTPResponse>, store: BaseStore) => {
  const { formErrorSync = {} } = store.afterAtFetch;
  const { disabled, statusCode, dataField } = mergeWithArrayOverride(cloneDeep(DF_FORM_ERROR_SYNC_CONFIG), formErrorSync) as Required<IFormErrorSyncConfig>;
  // 如果禁用了表单错误同步，直接返回
  if (disabled) {
    return;
  }

  // 检查是否为参数校验错误
  const code = res?.code?.toString();
  const isValidationError = code && (code === statusCode?.toString());
  if (!isValidationError) {
    return;
  }

  // 获取错误数据
  const errorData = res?.[dataField];
  // 错误数据必须是对象 其中 key 表示字段路径  value 表示错误消息
  if (!errorData || typeof errorData !== 'object' || Array.isArray(errorData)) {
    return;
  }

  // 将错误信息同步到表单
  try {
    // 遍历错误数据，设置表单字段错误
    Object.entries(errorData).forEach(([fieldPath, errorMessage]) => {
      if (typeof errorMessage === 'string' && errorMessage.trim()) {
        store.form?.setFieldState(fieldPath, (state) => {
          // 为了当用户重新输入时，重新使用前端的校验，需要模拟为输入错误。
          // 参考源码 packages/core/src/shared/internals.ts validateToFeedbacks 315 行
          const errFeedback = {
            triggerType: 'onInput',
            type: 'error' as FieldFeedbackTypes,
            code: 'ValidateError',
            messages: [errorMessage],
          } ;
          if (isVacuous(state.feedbacks) || !Array.isArray(state.feedbacks)) {
            state.feedbacks = [errFeedback];
          } else {
            state.feedbacks?.push?.(errFeedback);
          }
        });
      }
    });
  } catch (error) {
    console.warn('表单错误同步失败:', error);
  }
};

/**
 * 数据获取后的回调函数类型
 * @param res - HTTP 响应数据
 * @param store - 存储实例
 */
export type IAfterAtFetchFn = (res: Partial<IHTTPResponse>, store: BaseStore) => void;

/**
 * 表单错误同步配置接口
 * @remarks 用于配置后端参数校验失败时是否将错误信息同步到表单状态
 */
export interface IFormErrorSyncConfig {
  /** 是否禁用表单错误同步，默认 false（即启用同步） */
  disabled?: boolean;
  /** 判断是否为参数校验错误的状态码，默认 400 */
  statusCode?: number | string;
  /** 参数校验错误数据的字段名，默认 'metadata' */
  dataField?: string;
}

// 默认值
export const DF_FORM_ERROR_SYNC_CONFIG: Required<IFormErrorSyncConfig> = {
  disabled: false,
  statusCode: 400,
  dataField: 'metadata',
};

/**
 * 数据获取后的处理配置接口
 */
export interface IAfterAtFetch {
  /** 重置值的时机 */
  resetValues?: boolean | 'success' | 'fail';
  /** 通知消息 */
  notify?: boolean | string;
  /** 失败时的通知消息 */
  failNotify?: boolean | string;
  /** 成功时的通知消息 */
  successNotify?: boolean | string;
  /** 通用回调函数 */
  run?: IAfterAtFetchFn;
  /** 失败时的回调函数 */
  failRun?: IAfterAtFetchFn;
  /** 成功时的回调函数 */
  successRun?: IAfterAtFetchFn;

  /** 表单错误同步配置 */
  formErrorSync?: IFormErrorSyncConfig;

}

/**
 * 操作类型存储的默认 afterAtFetch 配置
 * @remarks 默认在成功时重置值并显示通知
 */
export const opStoreAfterAtFetch: IAfterAtFetch = {
  resetValues: 'success',
  notify: true,
};
